/**
 * batch-70 wagenpark-week (FO-528 + FO-529, spec 228 + 229) - gedeelde types,
 * labels, instellingen, request-schema's en DTO's.
 *
 * Eén bron voor de API (validatie, DTO's), de backoffice (wagenpark-werkruimte,
 * tankpassen) en de portal (Mijn voertuig: week-km, contract, onderhoud en de
 * toelichting op afwijkingen). Drie vaste regels, overal:
 *
 *   - Een schatting wordt nooit als meting gepresenteerd: elke km-waarde draagt
 *     zijn bron (`kmBron`, `verdelingBron`, `splitsingBron`).
 *   - Geld alleen voor kostprijsrollen: de DTO's met bedragen zijn backoffice-only
 *     en de API leegt de bedragvelden zonder kostprijs-rol; de portal krijgt nooit
 *     een bedrag.
 *   - Alles is een signaal, nooit een automatische correctie (uren, saldo, loon).
 */

import { z } from 'zod'
import { queryBoolean } from './zod-query'

// ─── Week-sleutels ───────────────────────────────────────────────────────────

/** ISO-week als tekst: `2026-W44`. */
export const WEEK_LABEL_REGEX = /^(\d{4})-W(\d{2})$/

export const weekLabelSchema = z
  .string()
  .regex(WEEK_LABEL_REGEX, 'Gebruik een ISO-week als jjjj-Www (bijvoorbeeld 2026-W44)')
  .refine((s) => {
    const m = WEEK_LABEL_REGEX.exec(s)
    const w = m ? Number(m[2]) : 0
    return w >= 1 && w <= 53
  }, 'Weeknummer moet tussen 1 en 53 liggen')

// ─── Km-bronnen en controles (FO-528 §A) ─────────────────────────────────────

/** Hoe de km van een week tot stand kwamen (de trend toont dit altijd). */
export const KM_TREND_BRONNEN = ['TELEMATICA', 'IMPORT', 'STANDEN', 'GEINTERPOLEERD', 'GEEN'] as const
export type KmTrendBron = (typeof KM_TREND_BRONNEN)[number]

export const KM_TREND_BRON_LABELS: Record<KmTrendBron, string> = {
  TELEMATICA: 'Gemeten (telematica)',
  IMPORT: 'Gemeten (telematica-import)',
  STANDEN: 'Gemeten (doorgegeven standen)',
  GEINTERPOLEERD: 'Geschat (tussen standen)',
  GEEN: 'Geen meting',
}

/** Een meting (tegenover een schatting of niets). */
export function isKmTrendMeting(bron: KmTrendBron): boolean {
  return bron === 'TELEMATICA' || bron === 'IMPORT' || bron === 'STANDEN'
}

export const KM_CONTROLE_SOORTEN = ['TELLER_TERUG', 'SPRONG', 'GAT'] as const
export type KmControleSoort = (typeof KM_CONTROLE_SOORTEN)[number]

export const KM_CONTROLE_SOORT_LABELS: Record<KmControleSoort, string> = {
  TELLER_TERUG: 'Teller lager dan vorige stand',
  SPRONG: 'Sprong boven het weekmaximum',
  GAT: 'Geen stand ontvangen',
}

export const KM_CONTROLE_STATUSSEN = ['OPEN', 'GEACCEPTEERD', 'GECORRIGEERD', 'GENEGEERD'] as const
export type KmControleStatus = (typeof KM_CONTROLE_STATUSSEN)[number]

export const KM_CONTROLE_STATUS_LABELS: Record<KmControleStatus, string> = {
  OPEN: 'Te beoordelen',
  GEACCEPTEERD: 'Geaccepteerd',
  GECORRIGEERD: 'Gecorrigeerd',
  GENEGEERD: 'Genegeerd',
}

export const KM_CONTROLE_BESLUITEN = ['ACCEPTEREN', 'CORRIGEREN', 'NEGEREN'] as const
export type KmControleBesluit = (typeof KM_CONTROLE_BESLUITEN)[number]

/** Beoordeel een verdachte stand: accepteren, corrigeren (met de juiste stand) of negeren. */
export const kmControleBeoordelenSchema = z
  .object({
    besluit: z.enum(KM_CONTROLE_BESLUITEN),
    reden: z.string().trim().min(3, 'Geef een reden van minstens 3 tekens').max(500),
    gecorrigeerdeKmStand: z.number().int().min(0).max(2_000_000).optional(),
  })
  .superRefine((v, ctx) => {
    if (v.besluit === 'CORRIGEREN' && v.gecorrigeerdeKmStand == null) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['gecorrigeerdeKmStand'], message: 'Vul de juiste km-stand in' })
    }
  })
export type KmControleBeoordelenInvoer = z.infer<typeof kmControleBeoordelenSchema>

export const RIT_CLASSIFICATIES = ['PRIVE', 'ZAKELIJK', 'WOONWERK', 'ONBEKEND'] as const
export type RitClassificatie = (typeof RIT_CLASSIFICATIES)[number]

export const RIT_CLASSIFICATIE_LABELS: Record<RitClassificatie, string> = {
  PRIVE: 'Privé',
  ZAKELIJK: 'Zakelijk',
  WOONWERK: 'Woon-werk',
  ONBEKEND: 'Onbekend',
}

export const VERDELING_BRONNEN = ['TELEMATICA', 'AFGELEID', 'GEEN'] as const
export type VerdelingBron = (typeof VERDELING_BRONNEN)[number]

export const VERDELING_BRON_LABELS: Record<VerdelingBron, string> = {
  TELEMATICA: 'Uit de ritten (telematica)',
  AFGELEID: 'Afgeleid (werkbonritten en woon-werk)',
  GEEN: 'Geen verdeling',
}

export const KM_TREND_MARKERINGEN = [
  /** Er zijn meer zakelijke km geschreven dan er gereden is: zakelijk = de week, privé 0. */
  'ZAKELIJK_BOVEN_TOTAAL',
  /** Lease zonder contract-km: geen rato en geen prognose. */
  'CONTRACT_KM_ONTBREEKT',
  /** De week heeft een open verdachte stand (die telt niet mee tot hij beoordeeld is). */
  'VERDACHTE_STAND',
  /** Meer dan één bestuurder in de week (verdeling per dag). */
  'BESTUURDERSWISSEL',
  /** Geen bestuurder (poolauto): km wel, verdeling per persoon niet. */
  'ZONDER_BESTUURDER',
] as const
export type KmTrendMarkering = (typeof KM_TREND_MARKERINGEN)[number]

export const KM_TREND_MARKERING_LABELS: Record<KmTrendMarkering, string> = {
  ZAKELIJK_BOVEN_TOTAAL: 'Meer zakelijke km geschreven dan gereden',
  CONTRACT_KM_ONTBREEKT: 'Contract-km ontbreekt',
  VERDACHTE_STAND: 'Verdachte stand te beoordelen',
  BESTUURDERSWISSEL: 'Bestuurderswissel in de week',
  ZONDER_BESTUURDER: 'Zonder bestuurder',
}

// ─── Rit versus uren (FO-528 §D) ─────────────────────────────────────────────

export const RIT_UREN_AFWIJKING_SOORTEN = [
  'REISTIJD_MEER_GESCHREVEN_DAN_GEREDEN',
  'REISTIJD_MINDER_GESCHREVEN',
  'REISKM_AFWIJKING',
  'EERDER_VERTROKKEN',
  'TE_LAAT_AANWEZIG',
  'AANWEZIGHEID_EERDER_GESCHREVEN',
  'REISTIJD_ZONDER_RIT',
] as const
export type RitUrenAfwijkingSoort = (typeof RIT_UREN_AFWIJKING_SOORTEN)[number]

export const RIT_UREN_AFWIJKING_SOORT_LABELS: Record<RitUrenAfwijkingSoort, string> = {
  REISTIJD_MEER_GESCHREVEN_DAN_GEREDEN: 'Meer reistijd geschreven dan gereden',
  REISTIJD_MINDER_GESCHREVEN: 'Minder reistijd geschreven dan gereden (ter info)',
  REISKM_AFWIJKING: 'Reis-km wijken af van de ritten',
  EERDER_VERTROKKEN: 'Eerder vertrokken dan geschreven',
  TE_LAAT_AANWEZIG: 'Later aangekomen dan gepland',
  AANWEZIGHEID_EERDER_GESCHREVEN: 'Begintijd geschreven vóór aankomst',
  REISTIJD_ZONDER_RIT: 'Reistijd geschreven zonder rit',
}

/** Informatief: telt niet als afwijking die om toelichting vraagt. */
export const RIT_UREN_INFORMATIEF: readonly RitUrenAfwijkingSoort[] = ['REISTIJD_MINDER_GESCHREVEN']

export const RIT_UREN_STATUSSEN = ['OPEN', 'TOEGELICHT', 'BEVESTIGD', 'GECORRIGEERD', 'GENEGEERD'] as const
export type RitUrenStatus = (typeof RIT_UREN_STATUSSEN)[number]

export const RIT_UREN_STATUS_LABELS: Record<RitUrenStatus, string> = {
  OPEN: 'Open',
  TOEGELICHT: 'Toegelicht',
  BEVESTIGD: 'Bevestigd',
  GECORRIGEERD: 'Gecorrigeerd (via uren-correctie)',
  GENEGEERD: 'Genegeerd',
}

export const RIT_HINTS = ['MOGELIJK_MEEGEREDEN'] as const
export type RitHint = (typeof RIT_HINTS)[number]

export const RIT_HINT_LABELS: Record<RitHint, string> = {
  MOGELIJK_MEEGEREDEN: 'Mogelijk meegereden: een collega op dezelfde werkbon reed wel',
}

export const TOELICHTING_REDENEN = ['MEERIJDEN', 'OMRIJDEN', 'FILE', 'POOLAUTO', 'PIKET', 'ANDERS'] as const
export type ToelichtingReden = (typeof TOELICHTING_REDENEN)[number]

export const TOELICHTING_REDEN_LABELS: Record<ToelichtingReden, string> = {
  MEERIJDEN: 'Meegereden met een collega',
  OMRIJDEN: 'Omgereden (omleiding, werk onderweg)',
  FILE: 'File of vertraging',
  POOLAUTO: 'Andere auto of poolauto gebruikt',
  PIKET: 'Piket of storingsdienst',
  ANDERS: 'Anders',
}

/** De bestuurder licht toe (portal). Bij ANDERS is een korte tekst verplicht. */
export const toelichtingSchema = z
  .object({
    reden: z.enum(TOELICHTING_REDENEN),
    toelichting: z.string().trim().max(500).optional(),
  })
  .superRefine((v, ctx) => {
    if (v.reden === 'ANDERS' && (v.toelichting ?? '').length < 3) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['toelichting'], message: 'Licht kort toe wat er gebeurde' })
    }
  })
export type ToelichtingInvoer = z.infer<typeof toelichtingSchema>

export const RIT_UREN_BESLUITEN = ['BEVESTIGD', 'GECORRIGEERD', 'GENEGEERD'] as const
export type RitUrenBesluit = (typeof RIT_UREN_BESLUITEN)[number]

/** Teamleider of planner beoordeelt (backoffice). Nooit een automatische correctie. */
export const ritUrenBeoordelenSchema = z.object({
  besluit: z.enum(RIT_UREN_BESLUITEN),
  reden: z.string().trim().min(3, 'Geef een reden van minstens 3 tekens').max(500),
})
export type RitUrenBeoordelenInvoer = z.infer<typeof ritUrenBeoordelenSchema>

// ─── Tankpassen (FO-529) ─────────────────────────────────────────────────────

// De statussen van een tankpas (incl. ONGEBRUIKT) staan in `./tankpas` (TANKPAS_STATUSSEN).

export const TANKPAS_HOUDER_BRONNEN = ['WISSEL', 'TOEWIJZING', 'INTREKKING', 'HANDMATIG', 'BACKFILL'] as const
export type TankpasHouderBron = (typeof TANKPAS_HOUDER_BRONNEN)[number]

export const TANKPAS_HOUDER_BRON_LABELS: Record<TankpasHouderBron, string> = {
  WISSEL: 'Bestuurderswissel',
  TOEWIJZING: 'Voertuig toegewezen',
  INTREKKING: 'Voertuig ingetrokken',
  HANDMATIG: 'Handmatig (pool- of reservepas)',
  BACKFILL: 'Afgeleid bij invoering',
}

export const TANKPAS_HOUDER_SOORTEN = ['VAST', 'TIJDELIJK', 'POOL'] as const
export type TankpasHouderSoort = (typeof TANKPAS_HOUDER_SOORTEN)[number]

/** Handmatige houder van een pool- of reservepas (geen voertuig), met reden. */
export const tankpasHandmatigeHouderSchema = z.object({
  employeeId: z.string().min(1, 'Kies een medewerker').nullable(),
  vanaf: z.string().min(1, 'Kies een datum'),
  reden: z.string().trim().min(3, 'Geef een reden van minstens 3 tekens').max(300),
})
export type TankpasHandmatigeHouderInvoer = z.infer<typeof tankpasHandmatigeHouderSchema>

export const TANK_REGIMES = ['BIJTELLING', 'GEEN_PRIVE', 'ONBEKEND'] as const
export type TankRegime = (typeof TANK_REGIMES)[number]

export const TANK_REGIME_LABELS: Record<TankRegime, string> = {
  BIJTELLING: 'Bijtelling (privé toegestaan)',
  GEEN_PRIVE: 'Geen bijtelling (alleen zakelijk)',
  ONBEKEND: 'Onbekend (keuze ontbreekt)',
}

/** Het regime van een weekcijfer: per houder, of gemengd bij een wissel in de week. */
export const TANK_WEEK_REGIMES = [...TANK_REGIMES, 'GEMENGD', 'GEEN_HOUDER'] as const
export type TankWeekRegime = (typeof TANK_WEEK_REGIMES)[number]

export const TANK_WEEK_REGIME_LABELS: Record<TankWeekRegime, string> = {
  ...TANK_REGIME_LABELS,
  GEMENGD: 'Gemengd (wissel in de week)',
  GEEN_HOUDER: 'Geen houder',
}

export const SPLITSING_BRONNEN = ['TELEMATICA', 'AFGELEID', 'EXPLICIET', 'GEEN'] as const
export type SplitsingBron = (typeof SPLITSING_BRONNEN)[number]

export const SPLITSING_BRON_LABELS: Record<SplitsingBron, string> = {
  TELEMATICA: 'Naar de ritten (telematica)',
  AFGELEID: 'Naar de afgeleide km-verdeling',
  EXPLICIET: 'Alleen expliciet privé gemarkeerde regels',
  GEEN: 'Geen splitsing (geen km-verdeling)',
}

/**
 * Waar de km van een tankpas-week vandaan komen (FO-529 §D11). Een geschatte weekstand (de
 * week-km tussen twee standen in) is GEEN meting: daarop volgt geen toelichtbaar
 * verbruikssignaal, en overal staat "geschat" bij (integrator-review H2).
 */
export const TANKPAS_KM_BRONNEN = ['TANKBON', 'WEEKSTAND', 'WEEKSTAND_GESCHAT', 'GEEN'] as const
export type TankpasKmBron = (typeof TANKPAS_KM_BRONNEN)[number]

export const TANKPAS_KM_BRON_LABELS: Record<TankpasKmBron, string> = {
  TANKBON: 'Km-standen op de tankbonnen (gemeten)',
  WEEKSTAND: 'Weekstand voertuig (gemeten)',
  WEEKSTAND_GESCHAT: 'Weekstand voertuig (geschat)',
  GEEN: 'Km onbekend',
}

/** Is de km van een tankpas-week gemeten (tankbon of een gemeten weekstand)? */
export function isTankpasKmMeting(bron: string | null | undefined): boolean {
  return bron === 'TANKBON' || bron === 'WEEKSTAND'
}

export const TANKPAS_VERBRUIK_HINTS = ['MEER_LITERS_DAN_KM', 'KM_ZONDER_LITERS', 'VERBRUIK_GESTEGEN'] as const
export type TankpasVerbruikHint = (typeof TANKPAS_VERBRUIK_HINTS)[number]

export const TANKPAS_VERBRUIK_HINT_LABELS: Record<TankpasVerbruikHint, string> = {
  MEER_LITERS_DAN_KM: 'Meer liters dan de km verklaren (tanken voor een ander voertuig of jerrycan?)',
  KM_ZONDER_LITERS: 'Km zonder liters (elders getankt of een andere pas?)',
  VERBRUIK_GESTEGEN: 'Verbruik sterk gestegen (techniek of rijgedrag?)',
}

export const TANKPAS_SIGNAAL_SOORTEN = [
  'GEBRUIK_ZONDER_HOUDER',
  'PRIVE_GEBRUIK_GEEN_BIJTELLING',
  'VERBRUIK_AFWIJKING',
  'TANKINHOUD_OVERSCHREDEN',
  'BUITEN_PLANNING',
  'WEEKEND_ZONDER_DIENST',
  'REGIME_ONBEKEND',
  'GRENS_500KM',
  // batch-70 review M8 (FO-529 §C9): tanken op een verlofdag zonder dienst, en een als privé
  // gemarkeerde factuurregel, bij geen bijtelling.
  'VERLOF_ZONDER_DIENST',
  'PRIVE_REGEL_GEEN_BIJTELLING',
] as const
export type TankpasSignaalSoort = (typeof TANKPAS_SIGNAAL_SOORTEN)[number]

export const TANKPAS_SIGNAAL_SOORT_LABELS: Record<TankpasSignaalSoort, string> = {
  GEBRUIK_ZONDER_HOUDER: 'Getankt zonder houder',
  PRIVE_GEBRUIK_GEEN_BIJTELLING: 'Privégebruik bij geen bijtelling',
  VERBRUIK_AFWIJKING: 'Verbruik wijkt af',
  TANKINHOUD_OVERSCHREDEN: 'Meer getankt dan de tank bevat',
  BUITEN_PLANNING: 'Getankt ver van de planning',
  WEEKEND_ZONDER_DIENST: 'Getankt in het weekend zonder dienst',
  REGIME_ONBEKEND: 'Bijtellingsregime onbekend',
  GRENS_500KM: '500 km-grens privégebruik',
  VERLOF_ZONDER_DIENST: 'Getankt op een verlofdag zonder dienst',
  PRIVE_REGEL_GEEN_BIJTELLING: 'Privé gemarkeerde tankbeurt bij geen bijtelling',
}

/** Signalen die de bestuurder zelf kan toelichten (de rest is beheer of HR). */
export const TANKPAS_SIGNAAL_TOELICHTBAAR: readonly TankpasSignaalSoort[] = [
  'PRIVE_GEBRUIK_GEEN_BIJTELLING',
  'VERBRUIK_AFWIJKING',
  'TANKINHOUD_OVERSCHREDEN',
  'BUITEN_PLANNING',
  'WEEKEND_ZONDER_DIENST',
  'VERLOF_ZONDER_DIENST',
  'PRIVE_REGEL_GEEN_BIJTELLING',
]

export const TANKPAS_SIGNAAL_STATUSSEN = ['OPEN', 'TOEGELICHT', 'BEVESTIGD', 'GENEGEERD'] as const
export type TankpasSignaalStatus = (typeof TANKPAS_SIGNAAL_STATUSSEN)[number]

export const TANKPAS_SIGNAAL_STATUS_LABELS: Record<TankpasSignaalStatus, string> = {
  OPEN: 'Open',
  TOEGELICHT: 'Toegelicht',
  BEVESTIGD: 'Bevestigd',
  GENEGEERD: 'Genegeerd',
}

export const TANKPAS_SIGNAAL_BESLUITEN = ['BEVESTIGD', 'GENEGEERD'] as const
export type TankpasSignaalBesluit = (typeof TANKPAS_SIGNAAL_BESLUITEN)[number]

/** Wagenpark of HR beoordeelt een signaal. Bevestigd privégebruik telt voor de 500 km-grens. */
export const tankpasSignaalBeoordelenSchema = z.object({
  besluit: z.enum(TANKPAS_SIGNAAL_BESLUITEN),
  reden: z.string().trim().min(3, 'Geef een reden van minstens 3 tekens').max(500),
})
export type TankpasSignaalBeoordelenInvoer = z.infer<typeof tankpasSignaalBeoordelenSchema>

// ─── Instellingen (WagenparkInstelling, sleutels `wagenpark.week.*`) ─────────

export const TELEMATICA_BRONNEN = ['IMPORT', 'LOG', 'CARTRACKER'] as const
export type TelematicaBron = (typeof TELEMATICA_BRONNEN)[number]

export const TELEMATICA_BRON_LABELS: Record<TelematicaBron, string> = {
  IMPORT: 'Wekelijkse import (bestand of km-rapport van de tracker)',
  LOG: 'Testgegevens (alleen lokaal en in de test)',
  CARTRACKER: 'Cartracker-koppeling (vereist API-toegang)',
}

export interface WagenparkWeekInstellingen {
  /** Een sprong boven dit aantal km per week is verdacht (A3: 3.000). */
  maxKmPerWeek: number
  /** Afwijking van de contract-km naar rato die een signaal geeft (A3: 10%). */
  contractAfwijkingPct: number
  /** Verwachte meerkm-kosten bij contracteinde die een signaal geven (euro, 500). */
  meerKmKostenDrempel: number
  /** Privé-km per week waarboven een signaal volgt (20.000 km per jaar = 385 per week). Zonder bijtelling telt elke km. */
  priveKmDrempelWeek: number
  /** Woon-werk telt als zakelijk voor de bijtelling en de 500 km-grens (A4: ja). */
  woonWerkTeltZakelijk: boolean
  /** Reistijd: afwijking in minuten of procent (A3: 20 min of 25%). */
  reistijdDrempelMin: number
  reistijdDrempelPct: number
  /** Reis-km: afwijking in km of procent (15 km of 25%). */
  reisKmDrempel: number
  reisKmDrempelPct: number
  /** Tolerantie voor vertrek en aankomst (A3: 15 min). */
  tolerantieMin: number
  /** Bewaartermijn van ritten (A5: 13 maanden). */
  ritBewaartermijnMaanden: number
  /** Verbruik: afwijking in procent of liters (A6: 20% of 15 l). */
  verbruikAfwijkingPct: number
  verbruikAfwijkingLiter: number
  /** Een tankbeurt verder dan dit van de planning van de dag (A6: 25 km). */
  maxAfstandVanPlanningKm: number
  /** De privé-km-grens per kalenderjaar bij geen bijtelling (500). */
  grens500Km: number
  /** Bij bijtelling wordt privé-brandstof verrekend (A3: nee, inbegrepen). */
  eigenBijdrageBrandstofPrive: boolean
  /** Het weekrapport gaat elke maandag uit. */
  weekrapportActief: boolean
  /**
   * Ritten uit telematica en de import opslaan (FO-528 §A1, A5). Standaard UIT tot de AVG/OR-toets
   * op de ritopslag rond is (integrator-review M1): dan komen alleen de km-standen binnen, is de
   * privé/zakelijk-verdeling afgeleid (werkbonritten en woon-werk) en is er geen vergelijking met de
   * uren. Een opgeslagen rit bewaart alleen de postcodecijfers (PC4) en de plaats.
   */
  rittenOpslaanActief: boolean
  /**
   * De vergelijking ritten versus uren (FO-528 §D) met persoonlijke meldingen. Standaard UIT
   * tot de AVG/OR-toets op de ritopslag rond is (A5, PO-beslispunt); het weekrapport meldt dat.
   * Werkt alleen met `rittenOpslaanActief`: zonder opgeslagen ritten is er niets te vergelijken.
   */
  ritVergelijkingActief: boolean
  /** Waar de wekelijkse km-standen vandaan komen. */
  telematicaBron: TelematicaBron
  /**
   * De goedgekeurde km-standen na goedkeuring mailen aan de leasemaatschappij (spec 242 B1).
   * Standaard UIT: goedkeuren legt dan alleen vast, er gaat niets naar buiten.
   */
  kmDelingWassinkActief: boolean
  /** Zoveel weken op rij een afwijking in de werk-km geeft een advies (spec 242 B3). */
  werkAfwijkingWeken: number
  /** Zoveel km per week telt als afwijking in de werk-km. */
  werkAfwijkingKm: number
  /** Onderhoud binnen zoveel dagen krijgt een voorstel om de afspraak klaar te zetten (spec 242 B4). */
  onderhoudVoorstelDagen: number
}

export const WAGENPARK_WEEK_DEFAULTS: WagenparkWeekInstellingen = {
  maxKmPerWeek: 3000,
  contractAfwijkingPct: 10,
  meerKmKostenDrempel: 500,
  priveKmDrempelWeek: 385,
  kmDelingWassinkActief: false,
  werkAfwijkingWeken: 3,
  werkAfwijkingKm: 50,
  onderhoudVoorstelDagen: 42,
  woonWerkTeltZakelijk: true,
  reistijdDrempelMin: 20,
  reistijdDrempelPct: 25,
  reisKmDrempel: 15,
  reisKmDrempelPct: 25,
  tolerantieMin: 15,
  ritBewaartermijnMaanden: 13,
  verbruikAfwijkingPct: 20,
  verbruikAfwijkingLiter: 15,
  maxAfstandVanPlanningKm: 25,
  grens500Km: 500,
  eigenBijdrageBrandstofPrive: false,
  weekrapportActief: true,
  rittenOpslaanActief: false,
  ritVergelijkingActief: false,
  telematicaBron: 'IMPORT',
}

/** De 500 km-grens geeft een melding bij deze percentages. */
export const GRENS_500KM_NIVEAUS = [60, 90, 100] as const

const geheel = (min: number, max: number) => z.number().int().min(min).max(max)

/** Opslaan van de instellingen (alle velden verplicht; de UI stuurt het hele blok). */
export const wagenparkWeekInstellingenSchema = z.object({
  maxKmPerWeek: geheel(100, 20_000),
  contractAfwijkingPct: z.number().min(0).max(100),
  meerKmKostenDrempel: z.number().min(0).max(100_000),
  priveKmDrempelWeek: geheel(0, 5_000),
  woonWerkTeltZakelijk: z.boolean(),
  reistijdDrempelMin: geheel(0, 600),
  reistijdDrempelPct: z.number().min(0).max(100),
  reisKmDrempel: geheel(0, 1_000),
  reisKmDrempelPct: z.number().min(0).max(100),
  tolerantieMin: geheel(0, 240),
  ritBewaartermijnMaanden: geheel(1, 60),
  verbruikAfwijkingPct: z.number().min(0).max(500),
  verbruikAfwijkingLiter: z.number().min(0).max(500),
  maxAfstandVanPlanningKm: geheel(0, 1_000),
  grens500Km: geheel(0, 10_000),
  eigenBijdrageBrandstofPrive: z.boolean(),
  weekrapportActief: z.boolean(),
  rittenOpslaanActief: z.boolean(),
  ritVergelijkingActief: z.boolean(),
  telematicaBron: z.enum(TELEMATICA_BRONNEN),
  kmDelingWassinkActief: z.boolean(),
  werkAfwijkingWeken: geheel(2, 26),
  werkAfwijkingKm: geheel(1, 5_000),
  onderhoudVoorstelDagen: geheel(7, 365),
})
export type WagenparkWeekInstellingenInvoer = z.infer<typeof wagenparkWeekInstellingenSchema>

export const kmDelingGoedkeurenSchema = z.object({ week: weekLabelSchema })
export type KmDelingGoedkeurenInvoer = z.infer<typeof kmDelingGoedkeurenSchema>

/** Zonder reminders: alles binnen de ingestelde termijn. */
export const onderhoudVoorstelSchema = z.object({ reminderIds: z.array(z.string().min(1)).min(1).max(500).optional() })
export type OnderhoudVoorstelInvoer = z.infer<typeof onderhoudVoorstelSchema>

// ─── Contract-km (FO-528 §B4) ────────────────────────────────────────────────

/** Eigendomsvormen waarbij contract-km verplicht zijn. */
export const LEASE_EIGENDOMMEN: readonly string[] = ['FINANCIAL_LEASE', 'OPERATIONAL_LEASE']

/** De contract-km van een voertuig bijwerken (PATCH; null = leegmaken). */
export const contractKmSchema = z.object({
  contractKmPerJaar: z.number().int().min(0).max(500_000).nullable(),
  contractKmStand: z.number().int().min(0).max(2_000_000).nullable(),
  meerKmTariefPerKm: z.number().min(0).max(100).nullable(),
  minderKmTariefPerKm: z.number().min(0).max(100).nullable(),
  tankInhoudLiter: z.number().min(0).max(2_000).nullable(),
})
export type ContractKmInvoer = z.infer<typeof contractKmSchema>

// ─── Lijst-queries ───────────────────────────────────────────────────────────

export const wagenparkWeekQuerySchema = z.object({
  week: weekLabelSchema.optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(50),
})

export const kmControleQuerySchema = wagenparkWeekQuerySchema.extend({
  status: z.enum(KM_CONTROLE_STATUSSEN).optional(),
  voertuigId: z.string().min(1).optional(),
  alleenOpen: queryBoolean().optional(),
})

export const ritUrenQuerySchema = wagenparkWeekQuerySchema.extend({
  status: z.enum(RIT_UREN_STATUSSEN).optional(),
  soort: z.enum(RIT_UREN_AFWIJKING_SOORTEN).optional(),
  employeeId: z.string().min(1).optional(),
  alleenOpen: queryBoolean().optional(),
})

export const tankpasSignaalQuerySchema = wagenparkWeekQuerySchema.extend({
  status: z.enum(TANKPAS_SIGNAAL_STATUSSEN).optional(),
  soort: z.enum(TANKPAS_SIGNAAL_SOORTEN).optional(),
  employeeId: z.string().min(1).optional(),
  fuelCardId: z.string().min(1).optional(),
  alleenOpen: queryBoolean().optional(),
})

// ─── DTO's ───────────────────────────────────────────────────────────────────

export interface KmControleDTO {
  id: string
  voertuigId: string
  kenteken: string
  soort: KmControleSoort
  bron: string
  datum: string
  week: string
  kmStand: number | null
  vorigeKmStand: number | null
  vorigeDatum: string | null
  kmPerWeek: number | null
  status: KmControleStatus
  gecorrigeerdeKmStand: number | null
  beoordelingReden: string | null
  beoordeeldDoor: string | null
  beoordeeldOp: string | null
}

/** Waarom de privé-km van een bestuurder in een week opvallen. Een indicatie, geen oordeel. */
export const PRIVE_KM_SIGNALEN = ['BOVEN_DREMPEL', 'GEEN_BIJTELLING', 'BIJTELLING_ONBEKEND'] as const
export type PriveKmSignaal = (typeof PRIVE_KM_SIGNALEN)[number]

export const PRIVE_KM_SIGNAAL_LABELS: Record<PriveKmSignaal, string> = {
  BOVEN_DREMPEL: 'Privé-km boven de weekdrempel',
  GEEN_BIJTELLING: 'Privé-km zonder bijtelling',
  BIJTELLING_ONBEKEND: 'Privé-km, bijtelling niet vastgelegd',
}

export interface KmTrendBestuurderDTO {
  employeeId: string
  naam: string | null
  dagen: number
  km: number | null
  priveKm: number | null
  zakelijkKm: number | null
  /** Bijtelling aan of uit op het einde van de week (keuze, verklaring, contract). */
  regime: TankRegime
  /** "Nee" op het contract zonder verklaring: het regime blijft ONBEKEND, maar HR ziet waar het vandaan komt. */
  contractGeenBijtelling: boolean
  priveSignalen: PriveKmSignaal[]
}

/**
 * Een week van een voertuig. Bedragen (`verwachteMeerKmKosten`,
 * `verwachteMinderKmOpbrengst`, tarieven) zijn null zonder kostprijs-rol.
 */
export interface KmTrendRijDTO {
  voertuigId: string
  kenteken: string
  merkModel: string
  week: string
  weekKm: number | null
  kmBron: KmTrendBron
  kmZekerheid: string | null
  /** De stand aan het begin van de week: de eindstand van de week ervoor. */
  kmStandBegin: number | null
  kmStandEind: number | null
  /** De km die de bestuurder volgens werkbonnen en planning voor het werk moest rijden. */
  geplandWerkKm: number | null
  /** Gemeten zakelijk min gepland. Alleen met ritten uit telematica; anders null (geen meting om mee te vergelijken). */
  werkAfwijkingKm: number | null
  cumulatiefKm: number | null
  contractKmPerJaar: number | null
  contractRatoKm: number | null
  afwijkingKm: number | null
  afwijkingPct: number | null
  prognoseEindKm: number | null
  contractTotaalKm: number | null
  verwachtMeerKm: number | null
  verwachtMinderKm: number | null
  verwachteMeerKmKosten: number | null
  verwachteMinderKmOpbrengst: number | null
  priveKm: number | null
  zakelijkKm: number | null
  woonWerkKm: number | null
  verdelingBron: VerdelingBron
  markeringen: KmTrendMarkering[]
  bestuurder: string | null
  bestuurders: KmTrendBestuurderDTO[]
}

export interface ContractKmOntbreektDTO {
  voertuigId: string
  kenteken: string
  merkModel: string
  eigendom: string
  leaseMaatschappij: string | null
  contractStart: string | null
  contractEind: string | null
  ontbreekt: Array<'contractKmPerJaar' | 'contractKmStand' | 'meerKmTariefPerKm' | 'minderKmTariefPerKm' | 'contractStart'>
}

export interface OnderhoudKomendDTO {
  voertuigId: string
  kenteken: string
  merkModel: string
  soort: string
  effectieveDatum: string | null
  vervalDatum: string | null
  vervalKm: number | null
  geschatteKmDatum: string | null
  bepalend: string | null
  /** Het weektempo (km/week, gemiddelde van de laatste 8 gemeten weken) of null. */
  weekTempo: number | null
  /** Bron van het tempo: TREND (weekstanden) of HISTORIE (km-historie van het jaar). */
  tempoBron: 'TREND' | 'HISTORIE' | null
  dagenTot: number | null
  kmTot: number | null
  afspraakGepland: boolean
  verlopen: boolean
  /** De reminder, om er een afspraak op klaar te zetten. */
  reminderId: string
  /** Er staat al een afspraak "te plannen" of "gepland" van deze soort. */
  openAfspraak: boolean
  /** De garage van het laatste onderhoud of de laatste afspraak op dit voertuig. */
  voorstelGarage: string | null
}

// ─── Km-standen goedkeuren en delen (spec 242 B1) ────────────────────────────

export const KM_DELING_STATUSSEN = ['OPEN', 'GOEDGEKEURD', 'VERSTUREN', 'VERSTUURD', 'MISLUKT'] as const
export type KmDelingStatus = (typeof KM_DELING_STATUSSEN)[number]

export const KM_DELING_STATUS_LABELS: Record<KmDelingStatus, string> = {
  OPEN: 'Wacht op goedkeuring',
  GOEDGEKEURD: 'Goedgekeurd, niet verstuurd',
  VERSTUREN: 'Wordt verstuurd',
  VERSTUURD: 'Verstuurd',
  MISLUKT: 'Versturen mislukt',
}

export interface KmDelingStandDTO {
  voertuigId: string
  kenteken: string
  kmStand: number
  op: string
  bron: string
}

export interface KmDelingOverzichtDTO {
  week: string
  status: KmDelingStatus
  /** Wat er gedeeld wordt (of, na goedkeuring, wat er vastligt). */
  standen: KmDelingStandDTO[]
  /** Leaseauto's zonder eigen stand in deze week: die gaan niet mee (een schatting is geen stand). */
  zonderStand: string[]
  /** Verdachte standen die eerst beoordeeld moeten worden. */
  verdacht: string[]
  ontvangers: { naam: string; to: string[]; cc: string[] }
  kanaalActief: boolean
  goedgekeurdDoor: string | null
  goedgekeurdOp: string | null
  verstuurdOp: string | null
  fout: string | null
}

// ─── Prognose per contractjaar (spec 242 B2) ─────────────────────────────────

export const CONTRACT_JAAR_STATUSSEN = ['AFGEROND', 'LOPEND', 'TOEKOMST'] as const
export type ContractJaarStatus = (typeof CONTRACT_JAAR_STATUSSEN)[number]

/** Eén volledig contractjaar, gerekend vanaf de contractdatum. */
export interface ContractJaarDTO {
  jaar: number
  start: string
  eind: string
  status: ContractJaarStatus
  /** De contract-km van dit jaar (naar rato als het contract eerder eindigt). */
  grensKm: number
  /** Gereden volgens de standen; null als een stand op de grens ontbreekt. */
  geredenKm: number | null
  /** Waar het jaar op uitkomt; bij een afgerond jaar de werkelijke km. */
  prognoseKm: number | null
  verschilKm: number | null
  /** WEEKTEMPO (laatste 8 weken), GEMIDDELDE (dit jaar tot nu) of null. */
  tempoBron: 'WEEKTEMPO' | 'GEMIDDELDE' | null
  /** Privé en zakelijk tellen alleen de weken met een verdeling. */
  priveKm: number
  zakelijkKm: number
  wekenMetVerdeling: number
}

export interface ContractJaarOverzichtDTO {
  voertuigId: string
  kenteken: string
  merkModel: string
  contractKmPerJaar: number
  jaren: ContractJaarDTO[]
}

// ─── Advies bij een structurele afwijking in de werk-km (spec 242 B3) ────────

export const WERK_ADVIES_SOORTEN = ['GEPLAND_BOVEN_GEREDEN', 'MEER_DAN_GEPLAND', 'MINDER_DAN_GEPLAND'] as const
export type WerkAdviesSoort = (typeof WERK_ADVIES_SOORTEN)[number]

export const WERK_ADVIES_TEKST: Record<WerkAdviesSoort, string> = {
  GEPLAND_BOVEN_GEREDEN:
    'Er staat structureel meer werk-km gepland dan de auto reed. Controleer de woon-werkafstand van de bestuurder, het reisadres en de werklocatie op de werkbonnen.',
  MEER_DAN_GEPLAND:
    'De ritten laten structureel meer werk-km zien dan gepland. Controleer of de reisroute en de werklocaties op de werkbonnen kloppen.',
  MINDER_DAN_GEPLAND:
    'De ritten laten structureel minder werk-km zien dan gepland. Controleer de woon-werkafstand en de reistijd op de werkbonnen.',
}

export interface WerkAdviesDTO {
  soort: WerkAdviesSoort
  weken: number
  gemiddeldKm: number
  /** De hoofdbestuurder in de laatste week, voor de link naar de gegevens die gecorrigeerd kunnen worden. */
  bestuurderId: string | null
}

export interface VoertuigAnalyseDTO {
  contractJaren: ContractJaarDTO[]
  werkAdvies: WerkAdviesDTO | null
}

export interface DatabronVoertuigDTO {
  voertuigId: string
  kenteken: string
  /** ZONDER_STAND = gekoppeld maar geen stand deze week; ZONDER_KOPPELING = geen telematica. */
  reden: 'ZONDER_STAND' | 'ZONDER_KOPPELING' | 'VERDACHT'
  laatsteStand: number | null
  laatsteStandOp: string | null
  laatsteBron: string | null
}

export interface RitUrenAfwijkingDTO {
  id: string
  employeeId: string
  medewerker: string
  voertuigId: string | null
  kenteken: string | null
  datum: string
  week: string
  soort: RitUrenAfwijkingSoort
  eenheid: 'MIN' | 'KM' | 'TIJD'
  geschreven: string | null
  gemeten: string | null
  verschil: number | null
  hint: RitHint | null
  status: RitUrenStatus
  toelichtingReden: ToelichtingReden | null
  toelichting: string | null
  toegelichtOp: string | null
  beoordeeldDoor: string | null
  beoordeeldOp: string | null
  beoordelingReden: string | null
  werkbonId: string | null
}

/** Portal: de eigen afwijking zonder ritdetails van anderen. */
export type MijnRitUrenAfwijkingDTO = Omit<RitUrenAfwijkingDTO, 'employeeId' | 'medewerker' | 'beoordeeldDoor'>

export interface TankpasHouderPeriodeDTO {
  id: string
  employeeId: string
  naam: string | null
  vanaf: string
  tot: string | null
  bron: TankpasHouderBron
  soort: TankpasHouderSoort
  kenteken: string | null
  reden: string | null
}

export interface TankpasSignaalDTO {
  id: string
  soort: TankpasSignaalSoort
  fuelCardId: string | null
  passnummerEinde: string | null
  kenteken: string | null
  employeeId: string | null
  medewerker: string | null
  week: string | null
  datum: string | null
  hint: TankpasVerbruikHint | null
  omschrijving: string
  status: TankpasSignaalStatus
  toelichting: string | null
  toegelichtOp: string | null
  beoordeeldDoor: string | null
  beoordeeldOp: string | null
  beoordelingReden: string | null
}

/** Een weekcijfer van een pas. Bedragen zijn null zonder kostprijs-rol. */
export interface TankpasWeekcijferDTO {
  fuelCardId: string
  passnummerEinde: string
  kenteken: string | null
  houderId: string | null
  houder: string | null
  week: string
  regime: TankWeekRegime
  tankbeurten: number
  liters: number
  bedragExclBtw: number | null
  prijsPerLiter: number | null
  kmGereden: number | null
  kmBron: TankpasKmBron
  kmGepland: number | null
  verbruikL100: number | null
  normL100: number | null
  normHerkomst: string | null
  afwijkingNormPct: number | null
  gemiddelde12wL100: number | null
  verbruikHint: TankpasVerbruikHint | null
  buitenWerktijd: number
  weekend: number
  buitenPlanning: number
  priveKm: number | null
  zakelijkKm: number | null
  priveKmAandeelPct: number | null
  bedragPrive: number | null
  bedragZakelijk: number | null
  splitsingBron: SplitsingBron
}

export interface TankpasKoppelingDTO {
  /** PAS_ZONDER_VOERTUIG, PAS_ZONDER_HOUDER, VOERTUIG_ZONDER_PAS, REGIME_ONBEKEND, REGELS_ONGEKOPPELD */
  soort: 'PAS_ZONDER_VOERTUIG' | 'PAS_ZONDER_HOUDER' | 'VOERTUIG_ZONDER_PAS' | 'REGIME_ONBEKEND' | 'REGELS_ONGEKOPPELD'
  omschrijving: string
  fuelCardId: string | null
  passnummerEinde: string | null
  voertuigId: string | null
  kenteken: string | null
  employeeId: string | null
  medewerker: string | null
  aantal: number | null
}

export interface WeekrapportSamenvatting {
  /** Wat iemand moet beoordelen: km-standen delen, structurele afwijkingen, onderhoud zonder afspraak. */
  teBeoordelen?: string[]
  week: string
  weekStart: string
  /** Top-5 contractoverschrijdingen (kenteken, afwijking %, gemeten of geschat). Bedragen alleen in de Excel. */
  topContract: Array<{ kenteken: string; afwijkingPct: number; prognoseEindKm: number | null; gemeten: boolean }>
  onderhoudDezeMaand: Array<{ kenteken: string; soort: string; effectieveDatum: string | null }>
  /** Het totaal (de lijst hierboven toont er hooguit 20; de rest staat in het tabblad Onderhoud). */
  onderhoudDezeMaandAantal: number
  afwijkingenPerSoort: Array<{ soort: RitUrenAfwijkingSoort; aantal: number }>
  topVerbruik: Array<{ kenteken: string; verbruikL100: number | null; afwijkingNormPct: number | null; hint: TankpasVerbruikHint | null; kmBron: TankpasKmBron }>
  priveBijGeenBijtelling: number
  ongekoppeldeRegels: number
  /** Voertuigen met gemeten week-km (telematica, import of doorgegeven standen). */
  voertuigenGemeten: number
  /** Voertuigen met geschatte week-km (tussen twee standen in): geen meting. */
  voertuigenGeschat: number
  /** Gekoppelde voertuigen (telematica) zonder km-stand in de week. */
  voertuigenZonderStand: number
  /** Alle tankpas-signalen van de week. */
  tankpasSignalen: number
  /** Precies wat ontbreekt: nooit stil overslaan (FO-528 §E11, FO-529 §E13). */
  ontbrekend: string[]
  laatsteBrandstoffactuurOp: string | null
}

export interface WeekrapportHistorieDTO {
  id: string
  week: string
  status: string
  verstuurdOp: string | null
  ontvangers: number
  bijlagenAlsLink: boolean
  fout: string | null
  samenvatting: WeekrapportSamenvatting | null
}

/** Portal: Mijn voertuig - de eigen week (geen bedragen, geen ritten). */
export interface MijnVoertuigWeekDTO {
  voertuigId: string
  kenteken: string
  weken: Array<{
    week: string
    weekKm: number | null
    kmBron: KmTrendBron
    /**
     * De auto had in die week meer bestuurders of reed niet de hele week voor deze medewerker:
     * `weekKm` is zijn deel naar dagen (een schatting, ook als de week zelf gemeten is).
     */
    aandeelNaarDagen: boolean
    priveKm: number | null
    zakelijkKm: number | null
    verdelingBron: VerdelingBron
  }>
  contract: {
    contractKmPerJaar: number | null
    cumulatiefKm: number | null
    contractRatoKm: number | null
    afwijkingPct: number | null
    prognoseEindKm: number | null
    contractTotaalKm: number | null
  } | null
  onderhoud: Array<{ soort: string; effectieveDatum: string | null; bepalend: string | null }>
  openToelichtingen: number
}

export interface TelematicaImportVoorbeeldDTO {
  id: string
  bestandsnaam: string | null
  status: string
  standen: number
  ritten: number
  /** Ritten in het bestand die niet zijn ingelezen: het opslaan van ritten staat uit (AVG/OR-toets). */
  rittenNietBewaard: number
  voertuigen: Array<{ kenteken: string; voertuigId: string | null; standen: number; ritten: number; verdacht: number; melding: string | null }>
  onbekend: string[]
  ongeldigeRegels: Array<{ regel: number; reden: string }>
}
